import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { useAuth } from 'hooks/useAuth';
import { MoonLoader } from 'react-spinners';

const ModalEditGroup = ({ isOpen, toggle, fetchGroups, name, groupDefault, groupId }) => {
  const { t: translate } = useTranslation();
  const firebaseHelper = getFirebaseBackend();
  const { user } = useAuth();
  const accountId = user.account;
  const [groupName, setGroupName] = useState(name);
  const [isDefault, setIsDefault] = useState(groupDefault);
  const [isSending, setIsSending] = useState(false);

  const onSubmit = async () => {
    try {
      setIsSending(true);

      if (isDefault) {
        await firebaseHelper.setGroupAsDefault(accountId, groupId);
      } else {
        await firebaseHelper.removeDefaultFromGroup(accountId, groupId);
      }

      await firebaseHelper.editKeywordGroup(accountId, groupId, groupName, isDefault);
      setGroupName('');
      setIsDefault(false);

      setTimeout(() => {
        fetchGroups();
        setIsSending(false);
        toggle();
      }, 1000);
    } catch (error) {
      setIsSending(false);
      console.log(error);
    }
  };

  const handleIsDefault = () => {
    setIsDefault(!isDefault);
  };

  return (
    <ModalAddGroupStyled isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader>{translate('Edit group')}</ModalHeader>
      <ModalBody>
        <div className="d-flex flex-column gap-3">
          <Input
            type="text"
            name="groupName"
            value={groupName}
            placeholder={translate('Group s name')}
            onChange={(e) => setGroupName(e.target.value)}
          />
          <Label check>
            <Input
              type="checkbox"
              name="isDefault"
              onChange={handleIsDefault}
              checked={isDefault}
            />
            {translate('Set this group as default')}
          </Label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          {translate('Cancel')}
        </Button>
        <Button
          type="submit"
          className="btn-submit d-flex align-items-center justify-content-center"
          disabled={!groupName}
          onClick={onSubmit}
        >
          {isSending ? <MoonLoader size={20} color="white" /> : translate('Save Changes')}
        </Button>
      </ModalFooter>
    </ModalAddGroupStyled>
  );
};

const ModalAddGroupStyled = styled(Modal)`
  min-width: 46rem;

  .modal-header {
    padding: 1rem;
  }

  .modal-body {
    padding: 1rem;

    label {
      margin: 0;
      input {
        margin-right: 0.5rem;
      }
    }
  }

  .modal-footer {
    padding: 1rem;

    button {
      height: 2.5rem;
    }

    .btn-submit {
      background-color: #48bb78;
      border: none;
      min-width: 6.25rem;
    }
    .btn-submit:hover {
      background-color: #2f8c58;
    }
  }
`;

ModalEditGroup.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  fetchGroups: PropTypes.func,
  name: PropTypes.string,
  groupDefault: PropTypes.bool,
  groupId: PropTypes.string,
};

const propsAreEqual = (prevProps, nextProps) => {
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.fetchGroups === nextProps.fetchGroups &&
    prevProps.toggle === nextProps.toggle &&
    prevProps.name === nextProps.name &&
    prevProps.groupDefault === nextProps.groupDefault &&
    prevProps.groupId === nextProps.groupId
  );
};

export default memo(ModalEditGroup, propsAreEqual);
